import { useState, useMemo, useEffect } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, CartesianGrid, Legend } from "recharts";
import CL from "./theme";
import AssistantBar from "./AssistantBar";

// ═══════════════════════════════════════════════════════════════
// C&S CHEMICALS — BID COMMAND CENTER v3 (Data-Driven)
// Powered by normalized JSON from Asana export
// ═══════════════════════════════════════════════════════════════

const fmt = n => n >= 1e6 ? (n / 1e6).toFixed(1) + "M" : n >= 1e3 ? (n / 1e3).toFixed(0) + "K" : String(n);
const TODAY = new Date();
const daysTo = d => d ? Math.ceil((new Date(d) - TODAY) / 864e5) : null;

const Card = ({ title, children, span, style: sx }) => (
  <div style={{ background: CL.card, border: `1px solid ${CL.border}`, borderRadius: 10, padding: 16, gridColumn: span ? `span ${span}` : undefined, ...sx }}>
    {title && <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: 2, color: CL.muted, textTransform: "uppercase", marginBottom: 12 }}>{title}</div>}
    {children}
  </div>
);

const Metric = ({ label, value, sub, color }) => (
  <div style={{ textAlign: "center" }}>
    <div style={{ fontSize: 26, fontWeight: 700, color: color || CL.text, fontFamily: "'JetBrains Mono',monospace", lineHeight: 1 }}>{value}</div>
    <div style={{ fontSize: 10, color: CL.muted, marginTop: 4 }}>{label}</div>
    {sub && <div style={{ fontSize: 9, color: CL.dim, marginTop: 2 }}>{sub}</div>}
  </div>
);

// ═══ EXECUTIVE TAB ═══
function Executive({ st, filtered }) {
  const stateData = useMemo(() => {
    const m = {};
    filtered.forEach(b => {
      const s = b.state || "Unknown";
      if (!m[s]) m[s] = { state: s, won: 0, lost: 0 };
      if (b.outcome === "Won") m[s].won++;
      if (b.outcome === "Lost") m[s].lost++;
    });
    return Object.values(m).filter(s => s.won + s.lost > 0).sort((a, b) => (b.won + b.lost) - (a.won + a.lost));
  }, [filtered]);

  const monthData = useMemo(() => {
    const m = {};
    filtered.forEach(b => {
      if (!b.bidReceived) return;
      const mo = b.bidReceived.slice(0, 7);
      if (!m[mo]) m[mo] = { month: mo, bids: 0, won: 0, lost: 0 };
      m[mo].bids++;
      if (b.outcome === "Won") m[mo].won++;
      if (b.outcome === "Lost") m[mo].lost++;
    });
    return Object.values(m).sort((a, b) => a.month.localeCompare(b.month)).slice(-12);
  }, [filtered]);

  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
      <Card><Metric label="Total Bids" value={st.t} color={CL.gold} sub={`${st.a.length} active`} /></Card>
      <Card><Metric label="Win Rate" value={st.wr + "%"} color={+st.wr >= 30 ? CL.won : CL.responded} sub={`${st.w.length}W / ${st.l.length}L`} /></Card>
      <Card><Metric label="Won Volume" value={fmt(st.wv) + " lbs"} color={CL.won} /></Card>
      <Card><Metric label="Pipeline Volume" value={fmt(st.av) + " lbs"} color={CL.active} sub={`${st.a.length + st.r.length} active bids`} /></Card>

      <Card title="Monthly Bid Activity" span={2}>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={monthData}>
            <CartesianGrid strokeDasharray="3 3" stroke={CL.border} />
            <XAxis dataKey="month" tick={{ fill: CL.muted, fontSize: 9 }} />
            <YAxis tick={{ fill: CL.muted, fontSize: 9 }} />
            <Tooltip contentStyle={{ background: CL.card, border: `1px solid ${CL.border}`, borderRadius: 8, fontSize: 11, color: CL.text }} />
            <Bar dataKey="won" fill={CL.won} name="Won" radius={[3, 3, 0, 0]} />
            <Bar dataKey="lost" fill={CL.lost} name="Lost" radius={[3, 3, 0, 0]} />
            <Legend wrapperStyle={{ fontSize: 10 }} />
          </BarChart>
        </ResponsiveContainer>
      </Card>

      <Card title="Outcome Distribution" span={2}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <ResponsiveContainer width="45%" height={175}>
            <PieChart>
              <Pie data={[
                { name: "Won", value: st.w.length },
                { name: "Lost", value: st.l.length },
                { name: "Active", value: st.a.length + st.r.length },
                { name: "No Bid", value: st.nb.length },
              ]} dataKey="value" cx="50%" cy="50%" innerRadius={38} outerRadius={68} paddingAngle={3}>
                <Cell fill={CL.won} /><Cell fill={CL.lost} /><Cell fill={CL.active} /><Cell fill={CL.noBid} />
              </Pie>
              <Tooltip contentStyle={{ background: CL.card, border: `1px solid ${CL.border}`, borderRadius: 8, fontSize: 11, color: CL.text }} />
            </PieChart>
          </ResponsiveContainer>
          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
            {[
              { l: "Won", c: CL.won, v: st.w.length },
              { l: "Lost", c: CL.lost, v: st.l.length },
              { l: "Active", c: CL.active, v: st.a.length + st.r.length },
              { l: "No Bid", c: CL.noBid, v: st.nb.length },
              { l: "Cancelled", c: CL.cancelled, v: st.cn.length },
            ].map(x => (
              <div key={x.l} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ width: 8, height: 8, borderRadius: 2, background: x.c }} />
                <span style={{ fontSize: 11, color: CL.dim, flex: 1 }}>{x.l}</span>
                <span style={{ fontFamily: "'JetBrains Mono'", fontSize: 12, fontWeight: 600 }}>{x.v}</span>
              </div>
            ))}
          </div>
        </div>
      </Card>

      <Card title="Win Rate by State" span={4}>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={stateData}>
            <CartesianGrid strokeDasharray="3 3" stroke={CL.border} />
            <XAxis dataKey="state" tick={{ fill: CL.muted, fontSize: 10 }} />
            <YAxis tick={{ fill: CL.muted, fontSize: 10 }} />
            <Tooltip contentStyle={{ background: CL.card, border: `1px solid ${CL.border}`, borderRadius: 8, fontSize: 11, color: CL.text }} />
            <Bar dataKey="won" fill={CL.won} name="Won" stackId="a" radius={[0, 0, 0, 0]} />
            <Bar dataKey="lost" fill={CL.lost} name="Lost" stackId="a" radius={[3, 3, 0, 0]} />
            <Legend wrapperStyle={{ fontSize: 10 }} />
          </BarChart>
        </ResponsiveContainer>
      </Card>
    </div>
  );
}

// ═══ BID BOARD (KANBAN) ═══
function BidBoard({ filtered, sel, setSel }) {
  const cols = [
    { k: "Active", l: "BID RECEIVED", c: CL.active, items: filtered.filter(b => b.outcome === "Active") },
    { k: "Responded", l: "RESPONDED", c: CL.responded, items: filtered.filter(b => b.outcome === "Responded") },
    { k: "Won", l: "WON", c: CL.won, items: filtered.filter(b => b.outcome === "Won").slice(0, 12) },
    { k: "Lost", l: "LOST", c: CL.lost, items: filtered.filter(b => b.outcome === "Lost").slice(0, 12) },
    { k: "No Bid", l: "NO BID", c: CL.noBid, items: filtered.filter(b => b.outcome === "No Bid").slice(0, 8) },
  ];

  return (
    <div style={{ display: "flex", gap: 10, overflowX: "auto", paddingBottom: 12 }}>
      {cols.map(col => (
        <div key={col.k} style={{ minWidth: 215, maxWidth: 255, flex: "1 0 215px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, padding: "0 4px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
              <span style={{ width: 7, height: 7, borderRadius: "50%", background: col.c }} />
              <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.5, color: CL.muted }}>{col.l}</span>
            </div>
            <span style={{ background: `${col.c}22`, color: col.c, borderRadius: 8, padding: "1px 7px", fontSize: 10, fontWeight: 700 }}>{col.items.length}</span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: "68vh", overflowY: "auto" }}>
            {col.items.map(b => {
              const d = daysTo(b.dueDate);
              const isSel = sel === b.id;
              return (
                <div key={b.id} onClick={() => setSel(isSel ? null : b.id)} style={{
                  background: isSel ? CL.bg : CL.card, border: `1px solid ${isSel ? col.c : CL.border}`,
                  borderLeft: `3px solid ${col.c}`, borderRadius: 7, padding: 10, cursor: "pointer", transition: "all 0.15s"
                }}>
                  <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 3 }}>{b.name}</div>
                  <div style={{ fontSize: 9, color: CL.muted, marginBottom: 4 }}>
                    {b.state || "?"} {b.year} {b.assignee ? `\u00b7 ${b.assignee.split(" ")[0]}` : ""}
                    {b.renewal && <span style={{ color: CL.gold, marginLeft: 4 }}>RENEWAL</span>}
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 3, marginBottom: 4 }}>
                    {(b.chemicals || []).slice(0, 2).map((c, i) => (
                      <span key={i} style={{ background: CL.bg, borderRadius: 3, padding: "1px 5px", fontSize: 8, color: CL.dim }}>{c}</span>
                    ))}
                    {(b.chemicals || []).length > 2 && <span style={{ fontSize: 8, color: CL.muted }}>+{b.chemicals.length - 2}</span>}
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    {b.volume ? <span style={{ fontFamily: "'JetBrains Mono'", fontSize: 9, color: CL.gold }}>{fmt(b.volume)} lbs</span> : <span style={{ fontSize: 9, color: CL.muted }}>&mdash;</span>}
                    {b.outcome === "Active" && d !== null && (
                      <span style={{ fontSize: 9, fontWeight: 700, color: d <= 0 ? "#ef4444" : d <= 7 ? "#f59e0b" : "#22c55e" }}>
                        {d <= 0 ? "OVERDUE" : d + "d"}
                      </span>
                    )}
                  </div>
                  {b.checklistPct > 0 && (
                    <div style={{ marginTop: 4 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 8, color: CL.muted, marginBottom: 2 }}>
                        <span>Checklist</span><span>{b.checklistPct}%</span>
                      </div>
                      <div style={{ height: 3, background: CL.bg, borderRadius: 2 }}>
                        <div style={{ height: 3, background: b.checklistPct === 100 ? CL.won : CL.active, borderRadius: 2, width: `${b.checklistPct}%` }} />
                      </div>
                    </div>
                  )}
                  {isSel && (
                    <div style={{ marginTop: 6, paddingTop: 6, borderTop: `1px solid ${CL.border}`, fontSize: 9, color: CL.dim }}>
                      {b.bidReceived && <div>Received: {b.bidReceived}</div>}
                      {b.dueDate && <div>Due: {b.dueDate}</div>}
                      {b.contractStart && <div>Contract: {b.contractStart} &rarr; {b.contractEnd || "?"}</div>}
                      {b.submissionMethod && <div>Submit: {b.submissionMethod}</div>}
                      {b.daysToRespond && <div>Response Window: {b.daysToRespond}d</div>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// ═══ INTELLIGENCE TAB ═══
function Intelligence({ st, filtered }) {
  const tierData = useMemo(() => [
    { label: "<50K", min: 0, max: 5e4 }, { label: "50K-250K", min: 5e4, max: 25e4 },
    { label: "250K-1M", min: 25e4, max: 1e6 }, { label: "1M-3M", min: 1e6, max: 3e6 },
    { label: "3M+", min: 3e6, max: Infinity },
  ].map(t => {
    const ib = filtered.filter(b => b.volume && b.volume >= t.min && b.volume < t.max);
    const w = ib.filter(b => b.outcome === "Won").length;
    const l = ib.filter(b => b.outcome === "Lost").length;
    const d = w + l;
    return { tier: t.label, won: w, lost: l, winRate: d > 0 ? Math.round(w / d * 100) : 0 };
  }), [filtered]);

  const ren = useMemo(() => {
    const rW = filtered.filter(b => b.outcome === "Won" && b.renewal).length;
    const rL = filtered.filter(b => b.outcome === "Lost" && b.renewal).length;
    const nW = filtered.filter(b => b.outcome === "Won" && !b.renewal).length;
    const nL = filtered.filter(b => b.outcome === "Lost" && !b.renewal).length;
    const rD = rW + rL, nD = nW + nL;
    return [
      { type: "Renewal", won: rW, lost: rL, wr: rD > 0 ? Math.round(rW / rD * 100) : 0 },
      { type: "New", won: nW, lost: nL, wr: nD > 0 ? Math.round(nW / nD * 100) : 0 },
    ];
  }, [filtered]);

  const resp = useMemo(() => {
    const w = filtered.filter(b => b.outcome === "Won" && b.daysToRespond > 0);
    const l = filtered.filter(b => b.outcome === "Lost" && b.daysToRespond > 0);
    return {
      aw: w.length ? Math.round(w.reduce((s, b) => s + b.daysToRespond, 0) / w.length) : 0,
      al: l.length ? Math.round(l.reduce((s, b) => s + b.daysToRespond, 0) / l.length) : 0,
    };
  }, [filtered]);

  const chemData = useMemo(() => {
    const m = {};
    filtered.forEach(b => {
      (b.chemicals || []).forEach(c => {
        if (!m[c]) m[c] = { chem: c, won: 0, lost: 0, total: 0 };
        m[c].total++;
        if (b.outcome === "Won") m[c].won++;
        if (b.outcome === "Lost") m[c].lost++;
      });
    });
    return Object.values(m).sort((a, b) => b.total - a.total).slice(0, 10);
  }, [filtered]);

  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
      <Card><Metric label="Avg Response (Won)" value={resp.aw + "d"} color={CL.won} sub="received to due" /></Card>
      <Card><Metric label="Avg Response (Lost)" value={resp.al + "d"} color={CL.lost} /></Card>
      <Card><Metric label="Renewal Win Rate" value={ren[0].wr + "%"} color={ren[0].wr >= 50 ? CL.won : CL.responded} sub={`${ren[0].won}W / ${ren[0].lost}L`} /></Card>
      <Card><Metric label="New Bid Win Rate" value={ren[1].wr + "%"} color={ren[1].wr >= 50 ? CL.won : CL.responded} sub={`${ren[1].won}W / ${ren[1].lost}L`} /></Card>

      <Card title="Win Rate by Volume Tier" span={2}>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={tierData} margin={{ bottom: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={CL.border} />
            <XAxis dataKey="tier" tick={{ fill: CL.muted, fontSize: 9 }} interval={0} angle={-20} textAnchor="end" height={40} />
            <YAxis tick={{ fill: CL.muted, fontSize: 9 }} />
            <Tooltip contentStyle={{ background: CL.card, border: `1px solid ${CL.border}`, borderRadius: 8, fontSize: 11, color: CL.text }} />
            <Bar dataKey="won" fill={CL.won} name="Won" radius={[3, 3, 0, 0]} />
            <Bar dataKey="lost" fill={CL.lost} name="Lost" radius={[3, 3, 0, 0]} />
            <Legend wrapperStyle={{ fontSize: 10 }} />
          </BarChart>
        </ResponsiveContainer>
        <div style={{ display: "flex", justifyContent: "center", gap: 14, marginTop: 6 }}>
          {tierData.map(t => (
            <div key={t.tier} style={{ textAlign: "center" }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: t.winRate >= 50 ? CL.won : CL.lost, fontFamily: "'JetBrains Mono'" }}>{t.winRate}%</div>
              <div style={{ fontSize: 8, color: CL.muted }}>{t.tier}</div>
            </div>
          ))}
        </div>
      </Card>

      <Card title="Top Chemicals Bid" span={2}>
        <ResponsiveContainer width="100%" height={340}>
          <BarChart data={chemData} layout="vertical" margin={{ left: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={CL.border} />
            <XAxis type="number" tick={{ fill: CL.muted, fontSize: 9 }} />
            <YAxis type="category" dataKey="chem" tick={{ fill: CL.dim, fontSize: 9 }} width={150} />
            <Tooltip contentStyle={{ background: CL.card, border: `1px solid ${CL.border}`, borderRadius: 8, fontSize: 11, color: CL.text }} />
            <Bar dataKey="won" fill={CL.won} name="Won" stackId="a" />
            <Bar dataKey="lost" fill={CL.lost} name="Lost" stackId="a" />
            <Legend wrapperStyle={{ fontSize: 10 }} />
          </BarChart>
        </ResponsiveContainer>
      </Card>

      <Card title="Sweet Spot Analysis" span={4}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <div style={{ background: CL.bg, borderRadius: 7, padding: 12, border: `1px solid ${CL.border}` }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: CL.won, marginBottom: 6 }}>WIN PROFILE</div>
            <div style={{ fontSize: 11, color: CL.dim, lineHeight: 1.7 }}>
              Volume under 2.5M lbs<br />
              SE corridor: GA, NC, AL, FL<br />
              Renewal relationships<br />
              Alum core + cross-sell chemicals<br />
              15-35 day response window
            </div>
          </div>
          <div style={{ background: CL.bg, borderRadius: 7, padding: 12, border: `1px solid ${CL.border}` }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: CL.lost, marginBottom: 6 }}>LOSS PROFILE</div>
            <div style={{ fontSize: 11, color: CL.dim, lineHeight: 1.7 }}>
              Volume over 3M (mfr direct)<br />
              VA / northern markets<br />
              Multi-chemical bundles (10+)<br />
              Reverse auctions<br />
              Response window &lt;7 days
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}

// ═══ CONTRACT CALENDAR ═══
function Calendar({ filtered }) {
  const contracts = useMemo(() =>
    filtered.filter(b => b.outcome === "Won" && b.contractEnd)
      .map(b => ({ ...b, dl: daysTo(b.contractEnd) }))
      .sort((a, b) => a.dl - b.dl),
    [filtered]
  );

  const exp = contracts.filter(c => c.dl < 0);
  const e90 = contracts.filter(c => c.dl >= 0 && c.dl <= 90);
  const e180 = contracts.filter(c => c.dl > 90 && c.dl <= 180);
  const safe = contracts.filter(c => c.dl > 180);

  const Sec = ({ title, items, color, tag }) => (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
        <span style={{ width: 8, height: 8, borderRadius: "50%", background: color }} />
        <span style={{ fontSize: 11, fontWeight: 700, color, letterSpacing: 1 }}>{title}</span>
        <span style={{ fontSize: 10, color: CL.muted }}>({items.length})</span>
      </div>
      {items.length === 0 ? (
        <div style={{ fontSize: 11, color: CL.muted, paddingLeft: 14 }}>None</div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 8 }}>
          {items.map(c => (
            <div key={c.id} style={{
              background: CL.card, border: `1px solid ${CL.border}`, borderLeft: `3px solid ${color}`,
              borderRadius: 7, padding: 10, display: "flex", justifyContent: "space-between", alignItems: "center"
            }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600 }}>{c.name}</div>
                <div style={{ fontSize: 9, color: CL.muted }}>{c.state} {c.volume ? `\u00b7 ${fmt(c.volume)} lbs` : ""}</div>
                <div style={{ fontSize: 9, color: CL.dim }}>{c.contractStart} &rarr; {c.contractEnd}</div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontFamily: "'JetBrains Mono'", fontSize: 13, fontWeight: 700, color }}>
                  {c.dl < 0 ? "EXPIRED" : c.dl + "d"}
                </div>
                <div style={{ fontSize: 8, color: CL.muted }}>{tag}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 16 }}>
        <Card><Metric label="Expired" value={exp.length} color={CL.lost} /></Card>
        <Card><Metric label="<90 Days" value={e90.length} color="#f59e0b" /></Card>
        <Card><Metric label="90-180 Days" value={e180.length} color={CL.active} /></Card>
        <Card><Metric label="180+ Days" value={safe.length} color={CL.won} /></Card>
      </div>
      <Sec title="EXPIRED" items={exp} color={CL.lost} tag="past due" />
      <Sec title="EXPIRING <90 DAYS" items={e90} color="#f59e0b" tag="renew now" />
      <Sec title="90-180 DAYS" items={e180} color={CL.active} tag="monitor" />
      <Sec title="SAFE 180+" items={safe} color={CL.won} tag="healthy" />
    </div>
  );
}

// ═══ PRICE INTELLIGENCE TAB ═══
const catColor = c => ({ tactical: CL.tactical, structural: CL.structural, margin: CL.margin, walkaway: CL.walkaway, specialty: CL.specialty, incumbent: CL.muted }[c] || CL.dim);

function PriceIntel({ filtered, priceIntel, sel, setSel, competitors = [], setTab }) {
  // Match price intel to bids by fuzzy name matching
  const lwi = useMemo(() => {
    const results = [];
    filtered.filter(b => b.outcome === "Lost").forEach(b => {
      // Try exact match first, then partial
      let pi = priceIntel[b.name];
      if (!pi) {
        // Try matching by partial name
        for (const [key, val] of Object.entries(priceIntel)) {
          if (b.name.includes(key.split(",")[0]) || key.includes(b.name.split(",")[0])) {
            pi = val; break;
          }
        }
      }
      if (pi) results.push({ ...b, pi });
    });
    return results.sort((a, b) => (a.pi.deltaPct || 99) - (b.pi.deltaPct || 99));
  }, [filtered, priceIntel]);

  const segs = useMemo(() => {
    const m = {};
    lwi.forEach(b => { m[b.pi.deltaCategory] = (m[b.pi.deltaCategory] || 0) + 1; });
    return Object.entries(m).filter(([, v]) => v > 0).map(([name, value]) => ({ name, value }));
  }, [lwi]);

  const comps = useMemo(() => {
    const m = {};
    Object.values(priceIntel).forEach(pi => {
      if (!pi.winBidder) return;
      if (!m[pi.winBidder]) m[pi.winBidder] = { name: pi.winBidder, wins: 0, deltas: [], states: new Set() };
      m[pi.winBidder].wins++;
      if (pi.deltaPct) m[pi.winBidder].deltas.push(pi.deltaPct);
    });
    Object.values(m).forEach(c => {
      c.avgD = c.deltas.length > 0 ? +(c.deltas.reduce((s, d) => s + d, 0) / c.deltas.length).toFixed(1) : 0;
    });
    return Object.values(m).sort((a, b) => b.wins - a.wins);
  }, [priceIntel]);

  const dHist = useMemo(() => [
    { label: "0-5%", min: 0, max: 5, color: CL.tactical },
    { label: "5-10%", min: 5, max: 10, color: CL.structural },
    { label: "10-20%", min: 10, max: 20, color: CL.margin },
    { label: "20-40%", min: 20, max: 40, color: CL.walkaway },
    { label: "40%+", min: 40, max: Infinity, color: "#991b1b" },
  ].map(bk => ({ ...bk, count: lwi.filter(b => b.pi.deltaPct >= bk.min && b.pi.deltaPct < bk.max).length })), [lwi]);

  const tl = filtered.filter(b => b.outcome === "Lost").length;
  const cov = tl > 0 ? Math.round(lwi.length / tl * 100) : 0;

  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
      <Card><Metric label="Intel Coverage" value={cov + "%"} color={cov >= 60 ? CL.won : CL.responded} sub={`${lwi.length} of ${tl} losses`} /></Card>
      <Card><Metric label="Tactical (<5%)" value={segs.find(s => s.name === "tactical")?.value || 0} color={CL.tactical} sub="winnable w/ pricing tweak" /></Card>
      <Card><Metric label="Structural (5-15%)" value={(segs.find(s => s.name === "structural")?.value || 0)} color={CL.structural} sub="needs pricing review" /></Card>
      <Card><Metric label="Walk-Away (15%+)" value={(segs.find(s => s.name === "walkaway")?.value || 0) + (segs.find(s => s.name === "margin")?.value || 0)} color={CL.walkaway} sub="mfr direct / cost gap" /></Card>

      <Card title="Loss Delta Distribution" span={2}>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={dHist}>
            <CartesianGrid strokeDasharray="3 3" stroke={CL.border} />
            <XAxis dataKey="label" tick={{ fill: CL.muted, fontSize: 10 }} />
            <YAxis tick={{ fill: CL.muted, fontSize: 10 }} allowDecimals={false} />
            <Tooltip contentStyle={{ background: CL.card, border: `1px solid ${CL.border}`, borderRadius: 8, fontSize: 11, color: CL.text }} />
            <Bar dataKey="count" name="Bids Lost" radius={[4, 4, 0, 0]}>
              {dHist.map((d, i) => <Cell key={i} fill={d.color} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
        <div style={{ textAlign: "center", marginTop: 6, fontSize: 10, color: CL.dim }}>
          <span style={{ color: CL.tactical, fontWeight: 700 }}>Green = fixable</span>{" \u2192 "}
          <span style={{ color: CL.structural, fontWeight: 700 }}>Yellow = structural</span>{" \u2192 "}
          <span style={{ color: CL.walkaway, fontWeight: 700 }}>Red = walk away</span>
        </div>
      </Card>

      <Card title="Competitor Profiles" span={2}>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {comps.map(c => {
            const profile = competitors.find(cp => cp.name === c.name);
            const threat = profile?.threatLevel;
            const tColor = threat === "high" ? CL.walkaway : threat === "medium" ? CL.structural : CL.won;
            return (
              <div key={c.name} style={{ background: CL.bg, borderRadius: 7, padding: 10, border: `1px solid ${CL.border}`, display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 34, height: 34, borderRadius: "50%", background: `${CL.lost}22`, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 13, color: CL.lost, flexShrink: 0 }}>{c.wins}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}>
                    {c.name}
                    {profile && <span style={{ fontSize: 8, padding: "1px 5px", borderRadius: 3, background: `${tColor}22`, color: tColor, fontWeight: 700, textTransform: "uppercase" }}>{threat}</span>}
                  </div>
                  {profile && <div style={{ fontSize: 9, color: CL.muted, marginTop: 1 }}>{profile.type === "public" ? profile.ticker + " \u00b7 " : ""}{profile.revenue}</div>}
                </div>
                <div style={{ textAlign: "right", flexShrink: 0 }}>
                  <div style={{ fontFamily: "'JetBrains Mono'", fontSize: 13, fontWeight: 700, color: c.avgD < 10 ? CL.structural : CL.walkaway }}>-{c.avgD}%</div>
                  <div style={{ fontSize: 8, color: CL.muted }}>avg delta</div>
                </div>
              </div>
            );
          })}
        </div>
        {competitors.length > 0 && setTab && (
          <button onClick={() => setTab("competitors")} style={{ marginTop: 10, width: "100%", background: "transparent", border: `1px solid ${CL.border}`, borderRadius: 6, padding: "6px 0", color: CL.gold, fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "'DM Sans',sans-serif" }}>
            View Full Competitor Profiles →
          </button>
        )}
      </Card>

      <Card title="Loss Detail \u2014 All Analyzed Bids" span={4}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${CL.border}` }}>
                {["Municipality", "St", "Yr", "Volume", "C&S", "Winner $", "Winner", "\u0394%", "Category", "Conf"].map(h => (
                  <th key={h} style={{ textAlign: "left", padding: "6px", fontSize: 9, fontWeight: 700, color: CL.muted, letterSpacing: 1, textTransform: "uppercase" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {lwi.map(b => {
                const cc = catColor(b.pi.deltaCategory);
                return (
                  <tr key={b.id} onClick={() => setSel(sel === b.id ? null : b.id)} style={{ borderBottom: `1px solid ${CL.border}`, cursor: "pointer", background: sel === b.id ? `${CL.active}11` : "transparent" }}>
                    <td style={{ padding: "6px", fontWeight: 600 }}>{b.name}</td>
                    <td style={{ padding: "6px", color: CL.dim }}>{b.state}</td>
                    <td style={{ padding: "6px", color: CL.dim }}>{b.year}</td>
                    <td style={{ padding: "6px", fontFamily: "'JetBrains Mono'", color: CL.gold }}>{b.volume ? fmt(b.volume) : "\u2014"}</td>
                    <td style={{ padding: "6px", fontFamily: "'JetBrains Mono'" }}>{b.pi.csPrice ? "$" + b.pi.csPrice : "\u2014"}</td>
                    <td style={{ padding: "6px", fontFamily: "'JetBrains Mono'", fontWeight: 700 }}>{b.pi.winPrice ? "$" + b.pi.winPrice : "\u2014"}</td>
                    <td style={{ padding: "6px", fontWeight: 600 }}>{b.pi.winBidder}</td>
                    <td style={{ padding: "6px", fontFamily: "'JetBrains Mono'", fontWeight: 700, color: cc }}>{b.pi.deltaPct !== null ? b.pi.deltaPct + "%" : "\u2014"}</td>
                    <td style={{ padding: "6px" }}><span style={{ background: `${cc}22`, color: cc, borderRadius: 3, padding: "2px 6px", fontSize: 9, fontWeight: 600 }}>{b.pi.deltaCategory}</span></td>
                    <td style={{ padding: "6px", color: CL.dim }}>{Math.round(b.pi.confidence * 100)}%</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// ═══ COMPETITORS TAB ═══
const STATES = ["GA", "NC", "AL", "SC", "FL", "VA", "TN"];
const threatColor = t => t === "high" ? CL.walkaway : t === "medium" ? CL.structural : CL.won;

function Competitors({ competitors, priceIntel, filtered, competitorIntel, onResearch }) {
  const [expandedId, setExpandedId] = useState(null);

  // Compute bid stats per competitor from price_intel
  const compStats = useMemo(() => {
    const m = {};
    Object.entries(priceIntel).forEach(([loc, pi]) => {
      if (!pi.winBidder) return;
      if (!m[pi.winBidder]) m[pi.winBidder] = { wins: 0, deltas: [], locations: [] };
      m[pi.winBidder].wins++;
      if (pi.deltaPct) m[pi.winBidder].deltas.push(pi.deltaPct);
      m[pi.winBidder].locations.push(loc);
    });
    Object.values(m).forEach(c => {
      c.avgD = c.deltas.length > 0 ? +(c.deltas.reduce((s, d) => s + d, 0) / c.deltas.length).toFixed(1) : 0;
    });
    return m;
  }, [priceIntel]);

  // Summary metrics
  const highThreat = competitors.filter(c => c.threatLevel === "high").length;
  const allDeltas = Object.values(compStats).flatMap(c => c.deltas);
  const avgDelta = allDeltas.length > 0 ? +(allDeltas.reduce((s, d) => s + d, 0) / allDeltas.length).toFixed(1) : 0;
  const mostActive = Object.entries(compStats).sort((a, b) => b[1].wins - a[1].wins)[0];

  // Chart data
  const chartData = competitors.map(c => ({
    name: c.name.length > 18 ? c.name.slice(0, 16) + "..." : c.name,
    fullName: c.name,
    wins: compStats[c.name]?.wins || 0,
    threat: c.threatLevel,
  })).sort((a, b) => b.wins - a.wins);

  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
      {/* Row 1: Metrics */}
      <Card><Metric label="Competitors Tracked" value={competitors.length} color={CL.gold} sub="across SE US markets" /></Card>
      <Card><Metric label="High Threat" value={highThreat} color={CL.walkaway} sub="require active monitoring" /></Card>
      <Card><Metric label="Avg Win Delta" value={avgDelta + "%"} color={CL.structural} sub="how much they undercut us" /></Card>
      <Card><Metric label="Most Active" value={mostActive ? mostActive[1].wins : 0} color={CL.lost} sub={mostActive ? mostActive[0] : "—"} /></Card>

      {/* Row 2: Win Chart + Geo Overlap */}
      <Card title="Competitor Win Count vs C&S" span={2}>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={chartData} layout="vertical" margin={{ left: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={CL.border} />
            <XAxis type="number" tick={{ fill: CL.muted, fontSize: 10 }} allowDecimals={false} />
            <YAxis type="category" dataKey="name" tick={{ fill: CL.dim, fontSize: 10 }} width={120} />
            <Tooltip contentStyle={{ background: CL.card, border: `1px solid ${CL.border}`, borderRadius: 8, fontSize: 11, color: CL.text }} />
            <Bar dataKey="wins" name="Wins vs C&S" radius={[0, 4, 4, 0]}>
              {chartData.map((d, i) => <Cell key={i} fill={threatColor(d.threat)} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Card>

      <Card title="Geographic Overlap Matrix" span={2}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 10 }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${CL.border}` }}>
                <th style={{ textAlign: "left", padding: 5, fontSize: 9, color: CL.muted, fontWeight: 700 }}>Competitor</th>
                {STATES.map(s => <th key={s} style={{ padding: 5, fontSize: 9, color: CL.muted, fontWeight: 700, textAlign: "center" }}>{s}</th>)}
              </tr>
            </thead>
            <tbody>
              {competitors.map(c => (
                <tr key={c.id} style={{ borderBottom: `1px solid ${CL.border}` }}>
                  <td style={{ padding: 5, fontWeight: 600, fontSize: 10, whiteSpace: "nowrap" }}>{c.name}</td>
                  {STATES.map(s => (
                    <td key={s} style={{ textAlign: "center", padding: 5 }}>
                      {c.geographicOverlap.includes(s)
                        ? <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: "50%", background: threatColor(c.threatLevel) }} />
                        : <span style={{ color: CL.border }}>—</span>}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Row 3: Expandable Profile Cards */}
      <Card title={"Competitor Profiles \u2014 " + competitors.length + " tracked"} span={4}>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {competitors.map(c => {
            const stats = compStats[c.name] || { wins: 0, avgD: 0, locations: [] };
            const intel = competitorIntel[c.id];
            const expanded = expandedId === c.id;
            const tCol = threatColor(c.threatLevel);
            return (
              <div key={c.id} style={{ background: CL.bg, borderRadius: 8, border: `1px solid ${expanded ? CL.gold + "44" : CL.border}`, overflow: "hidden" }}>
                {/* Collapsed row */}
                <div onClick={() => setExpandedId(expanded ? null : c.id)} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", cursor: "pointer" }}>
                  <div style={{ width: 34, height: 34, borderRadius: "50%", background: `${tCol}22`, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 12, color: tCol, flexShrink: 0 }}>
                    {c.type === "public" ? c.ticker?.slice(0, 3) : c.name.slice(0, 2).toUpperCase()}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, display: "flex", alignItems: "center", gap: 6 }}>
                      {c.name}
                      <span style={{ fontSize: 8, padding: "1px 5px", borderRadius: 3, background: `${tCol}22`, color: tCol, fontWeight: 700, textTransform: "uppercase" }}>{c.threatLevel}</span>
                      {c.type === "public" && <span style={{ fontSize: 8, padding: "1px 5px", borderRadius: 3, background: `${CL.specialty}22`, color: CL.specialty, fontWeight: 600 }}>{c.ticker}</span>}
                    </div>
                    <div style={{ fontSize: 10, color: CL.muted, marginTop: 1 }}>{c.hq} {c.revenue !== "Unknown" && c.revenue !== "Unknown (regional)" && c.revenue !== "Unknown (specialty niche)" ? " \u00b7 " + c.revenue : ""}</div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 16, flexShrink: 0 }}>
                    <div style={{ textAlign: "center" }}>
                      <div style={{ fontFamily: "'JetBrains Mono'", fontSize: 14, fontWeight: 700, color: CL.lost }}>{stats.wins}</div>
                      <div style={{ fontSize: 8, color: CL.muted }}>wins</div>
                    </div>
                    {stats.avgD > 0 && <div style={{ textAlign: "center" }}>
                      <div style={{ fontFamily: "'JetBrains Mono'", fontSize: 14, fontWeight: 700, color: stats.avgD < 10 ? CL.structural : CL.walkaway }}>-{stats.avgD}%</div>
                      <div style={{ fontSize: 8, color: CL.muted }}>avg delta</div>
                    </div>}
                    <div style={{ fontSize: 16, color: CL.muted, transition: "transform 0.2s", transform: expanded ? "rotate(180deg)" : "rotate(0)" }}>▾</div>
                  </div>
                </div>

                {/* Expanded detail */}
                {expanded && (
                  <div style={{ padding: "0 14px 14px", borderTop: `1px solid ${CL.border}` }}>
                    <div style={{ paddingTop: 12, fontSize: 12, color: CL.dim, lineHeight: 1.6, marginBottom: 12 }}>{c.description}</div>

                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                      <div>
                        <div style={{ fontSize: 10, fontWeight: 700, color: CL.won, marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 }}>Strengths</div>
                        {c.strengths.map((s, i) => <div key={i} style={{ fontSize: 11, color: CL.dim, paddingLeft: 10, borderLeft: `2px solid ${CL.won}33`, marginBottom: 3 }}>{s}</div>)}
                      </div>
                      <div>
                        <div style={{ fontSize: 10, fontWeight: 700, color: CL.lost, marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 }}>Weaknesses</div>
                        {c.weaknesses.map((w, i) => <div key={i} style={{ fontSize: 11, color: CL.dim, paddingLeft: 10, borderLeft: `2px solid ${CL.lost}33`, marginBottom: 3 }}>{w}</div>)}
                      </div>
                    </div>

                    <div style={{ display: "flex", gap: 12, marginBottom: 12 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: CL.muted, marginBottom: 4 }}>PRODUCTS OVERLAP</div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                          {c.products.map(p => <span key={p} style={{ fontSize: 9, background: `${CL.gold}15`, color: CL.gold, borderRadius: 3, padding: "2px 6px", fontWeight: 600 }}>{p}</span>)}
                        </div>
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: CL.muted, marginBottom: 4 }}>STATES</div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                          {c.geographicOverlap.map(s => <span key={s} style={{ fontSize: 9, background: `${CL.active}15`, color: CL.active, borderRadius: 3, padding: "2px 6px", fontWeight: 600 }}>{s}</span>)}
                        </div>
                      </div>
                    </div>

                    {stats.locations.length > 0 && (
                      <div style={{ marginBottom: 12 }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: CL.muted, marginBottom: 4 }}>BID WINS VS C&S</div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                          {stats.locations.map(l => <span key={l} style={{ fontSize: 9, background: `${CL.lost}15`, color: CL.lost, borderRadius: 3, padding: "2px 6px" }}>{l}</span>)}
                        </div>
                      </div>
                    )}

                    {c.notes && <div style={{ fontSize: 11, color: CL.responded, background: `${CL.responded}11`, borderRadius: 6, padding: "8px 12px", borderLeft: `3px solid ${CL.responded}`, marginBottom: 12 }}>📋 {c.notes}</div>}

                    {/* AI Research Section */}
                    <div style={{ borderTop: `1px solid ${CL.border}`, paddingTop: 10 }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: CL.gold, textTransform: "uppercase", letterSpacing: 0.5 }}>AI Intelligence</div>
                        <button onClick={(e) => { e.stopPropagation(); onResearch(c.id); }}
                          disabled={intel?.loading}
                          style={{ background: intel?.loading ? CL.card : CL.gold, border: "none", borderRadius: 5, color: "#fff", fontSize: 10, fontWeight: 700, padding: "5px 14px", cursor: intel?.loading ? "default" : "pointer", fontFamily: "'DM Sans',sans-serif", opacity: intel?.loading ? 0.6 : 1 }}>
                          {intel?.loading ? "Researching..." : intel?.marketAssessment ? "Refresh Research" : "Research " + c.name}
                        </button>
                      </div>
                      {intel?.error && <div style={{ fontSize: 11, color: CL.lost, marginBottom: 6 }}>Error: {intel.error}</div>}
                      {intel?.marketAssessment && (
                        <div>
                          <div style={{ fontSize: 11, color: CL.dim, lineHeight: 1.6, marginBottom: 8 }}>{intel.marketAssessment}</div>
                          {intel.newsItems?.length > 0 && (
                            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                              {intel.newsItems.map((n, i) => (
                                <div key={i} style={{ fontSize: 10, color: CL.dim, background: CL.card, borderRadius: 5, padding: "6px 10px", display: "flex", gap: 8, alignItems: "flex-start" }}>
                                  <span style={{ fontSize: 9, color: CL.muted, flexShrink: 0, fontFamily: "'JetBrains Mono'" }}>{n.date}</span>
                                  <span style={{ fontSize: 8, padding: "1px 4px", borderRadius: 2, background: `${CL.active}22`, color: CL.active, fontWeight: 600, flexShrink: 0 }}>{n.category}</span>
                                  <div><span style={{ fontWeight: 600, color: CL.text }}>{n.headline}</span> — {n.summary}</div>
                                </div>
                              ))}
                            </div>
                          )}
                          {intel.pricingSignals && <div style={{ fontSize: 10, color: CL.structural, marginTop: 6 }}>💰 Pricing: {intel.pricingSignals}</div>}
                          {intel.outlook && <div style={{ fontSize: 10, color: CL.gold, marginTop: 4 }}>📊 Outlook: {intel.outlook}</div>}
                          {intel.generatedAt && <div style={{ fontSize: 9, color: CL.muted, marginTop: 6 }}>Last researched: {new Date(intel.generatedAt).toLocaleDateString()}</div>}
                        </div>
                      )}
                      {!intel?.marketAssessment && !intel?.loading && !intel?.error && (
                        <div style={{ fontSize: 11, color: CL.muted, fontStyle: "italic" }}>Click "Research" to generate AI intelligence on this competitor.</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}

// ═══ DATA QUALITY TAB ═══
function DataQuality({ filtered }) {
  const gaps = useMemo(() => {
    const noVol = filtered.filter(b => !b.volume).length;
    const noChem = filtered.filter(b => !b.chemicals || b.chemicals.length === 0).length;
    const noEnd = filtered.filter(b => b.outcome === "Won" && !b.contractEnd).length;
    const noMethod = filtered.filter(b => b.submissionMethod === "Unknown").length;
    const total = filtered.length;
    return [
      { label: "Missing Volume", count: noVol, pct: Math.round(noVol / total * 100), color: noVol / total > 0.3 ? CL.lost : CL.responded },
      { label: "Missing Chemicals", count: noChem, pct: Math.round(noChem / total * 100), color: noChem / total > 0.5 ? CL.lost : CL.responded },
      { label: "Won - No Contract End", count: noEnd, pct: Math.round(noEnd / (filtered.filter(b => b.outcome === "Won").length || 1) * 100), color: noEnd > 5 ? CL.lost : CL.responded },
      { label: "Unknown Submission", count: noMethod, pct: Math.round(noMethod / total * 100), color: noMethod / total > 0.3 ? CL.lost : CL.responded },
    ];
  }, [filtered]);

  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
      {gaps.map(g => (
        <Card key={g.label}>
          <Metric label={g.label} value={g.count} color={g.color} sub={`${g.pct}% of bids`} />
          <div style={{ marginTop: 8, height: 4, background: CL.bg, borderRadius: 2 }}>
            <div style={{ height: 4, background: g.color, borderRadius: 2, width: `${Math.min(g.pct, 100)}%` }} />
          </div>
        </Card>
      ))}
      <Card title="Data Completeness Summary" span={4}>
        <div style={{ fontSize: 12, color: CL.dim, lineHeight: 1.8 }}>
          <strong style={{ color: CL.text }}>{filtered.length} bids</strong> normalized from Asana export.{" "}
          <strong style={{ color: CL.gold }}>{filtered.filter(b => b.renewal).length} renewals</strong> identified.{" "}
          <strong style={{ color: CL.won }}>{filtered.filter(b => b.volume).length} bids</strong> have volume data.{" "}
          <strong style={{ color: CL.responded }}>{filtered.filter(b => b.chemicals && b.chemicals.length > 0).length} bids</strong> have chemical details.{" "}
          All bids have checklist tracking data with completion percentages.
          <br /><br />
          <strong style={{ color: CL.crimson }}>Action Required:</strong> Remaining bids with missing chemicals only list
          {" \""}water treatment chemicals{"\""} generically in Asana notes. Specific chemical names need backfilling from actual bid documents.
          Contract end dates needed for renewal intelligence. See ownership document for full gap analysis.
        </div>
      </Card>
    </div>
  );
}

// ═══ MAIN APP ═══
// ═══ PASSWORD GATE WITH MULTI-USER SUPPORT ═══
// Passwords are stored as hashes — to add/change passwords, update access_config.json
// Generate a hash: open browser console on the site, type hashPass("yourpassword")
function hashPass(s) { let h = 0; for (let i = 0; i < s.length; i++) { h = ((h << 5) - h + s.charCodeAt(i)) | 0; } return Math.abs(h).toString(16); }
// Expose hash function for admin use (browser console: hashPass("newpassword"))
if (typeof window !== 'undefined') window.hashPass = hashPass;
// Fallback hashes baked into build (if access_config.json not found)
const FALLBACK_USERS = [
  { hash: hashPass("CS2026bid!"), role: "admin", label: "Admin" },
  { hash: hashPass("approach2026"), role: "admin", label: "Approach" },
  { hash: hashPass("csview"), role: "viewer", label: "Viewer" },
];

function PasswordGate({ onAuth }) {
  const [pw, setPw] = useState("");
  const [err, setErr] = useState(false);
  const [users, setUsers] = useState(FALLBACK_USERS);

  useEffect(() => {
    fetch("./access_config.json").then(r => r.json()).then(cfg => {
      if (cfg.users && cfg.users.length > 0) setUsers(cfg.users);
    }).catch(() => {}); // fallback to baked-in hashes
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    const inputHash = hashPass(pw);
    const match = users.find(u => u.hash === inputHash);
    if (match) {
      sessionStorage.setItem("cs_auth", "1");
      sessionStorage.setItem("cs_role", match.role || "viewer");
      sessionStorage.setItem("cs_user", match.label || "User");
      onAuth();
    } else {
      setErr(true);
      setPw("");
    }
  };
  return (
    <div style={{ fontFamily: "'DM Sans',sans-serif", background: CL.bg, color: CL.text, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <form onSubmit={handleSubmit} style={{ textAlign: "center", background: CL.card, border: `1px solid ${CL.border}`, borderRadius: 12, padding: 40, width: 360 }}>
        <div style={{ background: `linear-gradient(135deg, ${CL.gold}, ${CL.navy})`, width: 48, height: 48, borderRadius: 10, display: "inline-flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 20, color: "#fff", marginBottom: 16 }}>CS</div>
        <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 4 }}>Bid Command Center</div>
        <div style={{ fontSize: 12, color: CL.muted, marginBottom: 24 }}>C&S Chemicals &middot; Restricted Access</div>
        <input type="password" value={pw} onChange={e => { setPw(e.target.value); setErr(false); }} placeholder="Enter password"
          style={{ width: "100%", padding: "10px 14px", borderRadius: 6, border: `1px solid ${err ? CL.lost : CL.border}`, background: CL.bg, color: CL.text, fontSize: 14, marginBottom: 12, outline: "none" }}
          autoFocus />
        {err && <div style={{ fontSize: 11, color: CL.lost, marginBottom: 8 }}>Incorrect password</div>}
        <button type="submit" style={{ width: "100%", padding: "10px 0", borderRadius: 6, border: "none", background: CL.gold, color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Access Dashboard</button>
      </form>
    </div>
  );
}

export default function App() {
  const [authed, setAuthed] = useState(() => sessionStorage.getItem("cs_auth") === "1");
  const [bids, setBids] = useState([]);
  const [priceIntel, setPriceIntel] = useState({});
  const [competitors, setCompetitors] = useState([]);
  const [competitorIntel, setCompetitorIntel] = useState({});
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("executive");
  const [yf, setYf] = useState("all");
  const [sel, setSel] = useState(null);

  useEffect(() => {
    if (!authed) return;
    Promise.all([
      fetch("./bids_normalized.json").then(r => r.json()),
      fetch("./price_intel.json").then(r => r.json()).catch(() => ({})),
      fetch("./competitors.json").then(r => r.json()).catch(() => ({ competitors: [] })),
    ]).then(([bidData, piData, compData]) => {
      setBids(bidData);
      setPriceIntel(piData);
      setCompetitors(compData.competitors || []);
      setLoading(false);
    }).catch(err => { console.error("Failed to load data:", err); setLoading(false); });
  }, [authed]);

  // Load cached competitor intel when Competitors tab is activated
  useEffect(() => {
    if (tab === "competitors" && Object.keys(competitorIntel).length === 0) {
      fetch("/.netlify/functions/competitor-intel")
        .then(r => r.json())
        .then(data => { if (data && typeof data === "object") setCompetitorIntel(data); })
        .catch(() => {});
    }
  }, [tab, competitorIntel]);

  const researchCompetitor = async (competitorId) => {
    setCompetitorIntel(prev => ({ ...prev, [competitorId]: { ...(prev[competitorId] || {}), loading: true } }));
    try {
      const comp = competitors.find(c => c.id === competitorId);
      const res = await fetch("/.netlify/functions/competitor-intel", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ competitorId, competitorName: comp?.name, companyType: comp?.type, ticker: comp?.ticker }),
      });
      const data = await res.json();
      setCompetitorIntel(prev => ({ ...prev, [competitorId]: { ...data, loading: false } }));
    } catch (err) {
      setCompetitorIntel(prev => ({ ...prev, [competitorId]: { error: err.message, loading: false } }));
    }
  };

  const filtered = useMemo(() => yf === "all" ? bids : bids.filter(b => b.year === +yf), [yf, bids]);

  const st = useMemo(() => {
    const w = filtered.filter(b => b.outcome === "Won");
    const l = filtered.filter(b => b.outcome === "Lost");
    const a = filtered.filter(b => b.outcome === "Active");
    const r = filtered.filter(b => b.outcome === "Responded");
    const nb = filtered.filter(b => b.outcome === "No Bid");
    const cn = filtered.filter(b => b.outcome === "Cancelled");
    const d = w.length + l.length;
    return {
      w, l, a, r, nb, cn, d,
      wr: d > 0 ? (w.length / d * 100).toFixed(0) : 0,
      wv: w.reduce((s, b) => s + (b.volume || 0), 0),
      lv: l.reduce((s, b) => s + (b.volume || 0), 0),
      av: a.reduce((s, b) => s + (b.volume || 0), 0),
      t: filtered.length,
    };
  }, [filtered]);

  const tabs = [
    { k: "executive", l: "Executive", i: "\u25C9" },
    { k: "operations", l: "Bid Board", i: "\u25A6" },
    { k: "intelligence", l: "Intelligence", i: "\u25C8" },
    { k: "priceintel", l: "Price Intel", i: "\u25C6" },
    { k: "calendar", l: "Contracts", i: "\u25A3" },
    { k: "quality", l: "Data Quality", i: "\u25C7" },
    { k: "competitors", l: "Competitors", i: "\u25CE" },
  ];

  if (!authed) return <PasswordGate onAuth={() => setAuthed(true)} />;

  if (loading) {
    return (
      <div style={{ fontFamily: "'DM Sans', sans-serif", background: CL.bg, color: CL.text, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 24, fontWeight: 700, color: CL.gold, marginBottom: 8 }}>C&S Chemicals</div>
          <div style={{ fontSize: 14, color: CL.muted }}>Loading Bid Command Center...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: "'DM Sans','Segoe UI',sans-serif", background: CL.bg, color: CL.text, minHeight: "100vh" }}>
      {/* HEADER */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 20px", borderBottom: `1px solid ${CL.border}` }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
          <div style={{ background: `linear-gradient(135deg, ${CL.gold}, ${CL.navy})`, width: 32, height: 32, borderRadius: 7, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 14, color: "#fff" }}>CS</div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, letterSpacing: 0.5 }}>Bid Command Center</div>
            <div style={{ fontSize: 10, color: CL.muted }}>C&S Chemicals {"\u00b7"} {bids.length} bids tracked {"\u00b7"} v3.0 Data-Driven</div>
          </div>
        </div>
        <AssistantBar bids={bids} priceIntel={priceIntel} />
        <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
          {["all", "2026", "2025", "2024"].map(y => (
            <button key={y} onClick={() => setYf(y)} style={{
              background: yf === y ? CL.gold + "22" : "transparent", border: `1px solid ${yf === y ? CL.gold : CL.border}`,
              color: yf === y ? CL.gold : CL.muted, borderRadius: 6, padding: "4px 12px", fontSize: 11, fontWeight: 600, cursor: "pointer",
            }}>{y === "all" ? "All Years" : y}</button>
          ))}
        </div>
      </div>

      {/* TABS */}
      <div style={{ display: "flex", gap: 2, padding: "8px 20px", borderBottom: `1px solid ${CL.border}` }}>
        {tabs.map(t => (
          <button key={t.k} onClick={() => setTab(t.k)} style={{
            background: tab === t.k ? CL.card : "transparent", border: `1px solid ${tab === t.k ? CL.border : "transparent"}`,
            color: tab === t.k ? CL.text : CL.muted, borderRadius: "6px 6px 0 0", padding: "6px 16px", fontSize: 11, fontWeight: 600, cursor: "pointer",
            borderBottom: tab === t.k ? `2px solid ${CL.gold}` : "2px solid transparent",
          }}>{t.i} {t.l}</button>
        ))}
      </div>

      {/* CONTENT */}
      <div style={{ padding: 20 }}>
        {tab === "executive" && <Executive st={st} filtered={filtered} />}
        {tab === "operations" && <BidBoard filtered={filtered} sel={sel} setSel={setSel} />}
        {tab === "intelligence" && <Intelligence st={st} filtered={filtered} />}
        {tab === "priceintel" && <PriceIntel filtered={filtered} priceIntel={priceIntel} sel={sel} setSel={setSel} competitors={competitors} setTab={setTab} />}
        {tab === "calendar" && <Calendar filtered={filtered} />}
        {tab === "quality" && <DataQuality filtered={filtered} />}
        {tab === "competitors" && <Competitors competitors={competitors} priceIntel={priceIntel} filtered={filtered} competitorIntel={competitorIntel} onResearch={researchCompetitor} />}
      </div>
    </div>
  );
}
